import type { Reconciliation } from '../types/api';
import type { AlgorithmEntry, BatchRequest } from '../types/compute';
import { executedInstanceIds } from './algorithmInstance';

/**
 * The config a stored result was actually produced by.
 *
 * A reconciliation carries TWO configs. `config` is what the user last saved;
 * `executionConfig` is what the worker ran, frozen by the backend inside the same
 * transaction that dispatched it (`execution_config = config`, reconciliations
 * store). Correlating a result against `config` is only right while nobody has
 * saved since — and saving without re-executing is a normal thing to do, since a
 * config edit does not invalidate the figures already on screen.
 *
 * Why it matters, concretely: the walk pairs each result envelope with a config
 * entry BY POSITION among the entries of that algorithm (resultToDashboard's
 * nthAlgorithmIndex), because an envelope carries no identity of its own. Delete
 * the first of two histograms, save, and the saved config has one entry where the
 * result has two envelopes — so the first envelope reads the SURVIVOR's parameters
 * and its figure is drawn, and captioned, from the wrong analysis. In a document
 * that goes to a client that is the worst failure available, and it is silent.
 *
 * The fallback is the old behaviour, for a row executed before the backend started
 * freezing (the column is nullable and `omitempty` keeps it off the wire when
 * unset). Nothing bridges it: those rows are disposable.
 */
export function executedConfigOf(rec: Reconciliation | null | undefined): BatchRequest | undefined {
  return rec?.executionConfig ?? rec?.config;
}

/**
 * The presentation-only fields a saved entry overrides on its executed twin. Each
 * changes how a stored result is drawn, never a number, so none is part of the
 * staleness check (`IGNORED_KEYS`) and a change to any is saved without re-running.
 * `figure_title` travels with `plot_measures` because changing the measures
 * re-mints it ("Tonelaje - Ley" → "Metal"): a chart redrawn with the new series
 * under the executed name would contradict itself.
 */
const PRESENTATION_FIELDS = ['plot_measures', 'figure_title', 'error_tolerance'] as const;

/**
 * The config a stored result is DRAWN with: the executed one, except that each
 * entry's `PRESENTATION_FIELDS` come from the saved entry with the same instance
 * id (SPEC/2515).
 *
 * The executed config still carries the values from the last run, so drawing from
 * it ignored a saved change until a re-run. Only these fields are lifted, and only
 * by instance id: everything else still pairs by position against what ran (see
 * above), and an unsaved-but-unexecuted parameter must not caption numbers it did
 * not produce. Both sides resolve ids through `executedInstanceIds`, so a row run
 * before instance ids existed pairs by the same positional id its saved config got.
 *
 * Returns the executed config itself when nothing differs, so an ordinary
 * reconciliation derives exactly as before.
 */
export function presentedConfigOf(rec: Reconciliation | null | undefined): BatchRequest | undefined {
  const executed = executedConfigOf(rec);
  if (!executed || !rec?.config || executed === rec.config) return executed;
  const savedEntries = rec.config.algorithms ?? [];
  const savedById = new Map<string, AlgorithmEntry>();
  // First occurrence wins: a lowered by-category fan-out repeats one id.
  executedInstanceIds(rec.config).forEach((id, i) => {
    if (!savedById.has(id)) savedById.set(id, savedEntries[i]);
  });
  const executedIds = executedInstanceIds(executed);
  let changed = false;
  const algorithms = executed.algorithms.map((a, i) => {
    const saved = savedById.get(executedIds[i]);
    if (!saved) return a;
    const differing = PRESENTATION_FIELDS.filter(
      (f) => JSON.stringify(a[f]) !== JSON.stringify(saved[f])
    );
    if (differing.length === 0) return a;
    changed = true;
    const next: AlgorithmEntry = { ...a };
    for (const f of differing) {
      if (saved[f] === undefined) delete next[f];
      else Object.assign(next, { [f]: saved[f] });
    }
    return next;
  });
  return changed ? { ...executed, algorithms } : executed;
}
